/**
 * ShapeTypes.ts
 * Types pour les formes du canvas
 * Source unique de vérité pour les données des shapes
 */
import { z } from 'zod';

/**
 * Types de formes supportés
 */
export type ShapeType =
  | 'rect'
  | 'circle'
  | 'text'
  | 'image'
  | 'triangle'
  | 'star'
  | 'heart'
  | 'arrow'
  | 'line'
  | 'group'
  | 'photoSpace';

/**
 * Données d'une forme
 */
export interface ShapeData {
  id: string;
  type: ShapeType;
  x: number;
  y: number;
  width?: number;
  height?: number;
  rotation?: number;
  opacity?: number;

  // Apparence
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;

  // Texte
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  fontStyle?: string;
  fontWeight?: string | number; // 'normal' | 'bold' | '600' | 400 | 700, etc.
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  /** Alias court de `textAlign` accepté par les presets historiques (cf. `data/defaultTemplates.ts`). */
  align?: 'left' | 'center' | 'right' | 'justify';
  /** Marqueur pour les espaces-photo dans les presets (cf. `data/defaultTemplates.ts`). */
  photoPlaceholder?: boolean;
  textDecoration?: string;
  textBackgroundColor?: string;
  textBackgroundPadding?: number;
  textBackgroundCornerRadius?: number;

  // Image
  src?: string;
  cropData?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };

  // Groupe
  children?: ShapeData[];

  // Photo space (Phase 4.1)
  photoIndex?: number; // Index de la photo (ordre)
  fitMode?: 'cover' | 'contain'; // Mode de remplissage
  maskRadius?: number; // Rayon des coins arrondis
  frame?: {
    width: number;
    color: string;
  }; // Cadre autour de la photo
  dropShadow?: {
    blur: number;
    offsetX: number;
    offsetY: number;
    color: string;
  }; // Ombre portée

  // Métadonnées
  name?: string;
  locked?: boolean; // PR-17: Verrouillé (pas modifiable)
  hidden?: boolean; // PR-17: Masqué (pas rendu)
  visible?: boolean;
  zIndex?: number;
  layer?: 'background' | 'photos' | 'overlay'; // Calque logique pour l'export photobooth

  // Ombres et effets
  shadowBlur?: number;
  shadowColor?: string;
  shadowOffsetX?: number;
  shadowOffsetY?: number;

  // Blend modes (modes de fusion)
  blendMode?: BlendMode;

  // Variables de texte
  textVariables?: Record<string, string>;

  // PR-16: Grouping
  groupId?: string | null;
}

/**
 * Modes de fusion (blend modes) supportés
 * Tous les modes supportés par Konva/Canvas globalCompositeOperation
 */
export type BlendMode =
  | 'normal' // source-over (par défaut)
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'color-dodge'
  | 'color-burn'
  | 'hard-light'
  | 'soft-light'
  | 'difference'
  | 'exclusion'
  | 'hue'
  | 'saturation'
  | 'color'
  | 'luminosity';

/**
 * Schéma Zod pour validation des shapes
 */
export const ShapeDataSchema = z.object({
  id: z.string().min(1),
  type: z.enum([
    'rect',
    'circle',
    'text',
    'image',
    'triangle',
    'star',
    'heart',
    'arrow',
    'line',
    'group',
    'photoSpace',
  ]),
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().positive().finite().optional(),
  height: z.number().positive().finite().optional(),
  rotation: z.number().finite().optional(),
  opacity: z.number().min(0).max(1).optional(),
  fill: z.string().optional(),
  stroke: z.string().optional(),
  strokeWidth: z.number().min(0).optional(),
  cornerRadius: z.number().min(0).optional(),
  text: z.string().max(10000).optional(),
  fontSize: z.number().min(1).max(1000).optional(),
  fontFamily: z.string().max(100).optional(),
  fontStyle: z.string().optional(),
  textAlign: z.enum(['left', 'center', 'right', 'justify']).optional(),
  textDecoration: z.string().optional(),
  textBackgroundColor: z.string().optional(),
  textBackgroundPadding: z.number().min(0).optional(),
  textBackgroundCornerRadius: z.number().min(0).optional(),
  shadowBlur: z.number().min(0).optional(),
  shadowColor: z.string().optional(),
  shadowOffsetX: z.number().optional(),
  shadowOffsetY: z.number().optional(),
  textVariables: z.record(z.string(), z.string()).optional(),
  src: z.string().optional(),
  children: z.array(z.lazy(() => ShapeDataSchema)).optional(),
  photoIndex: z.number().int().min(1).max(10).optional(),
  name: z.string().max(200).optional(),
  locked: z.boolean().optional(),
  visible: z.boolean().optional(),
  zIndex: z.number().int().optional(),
  layer: z.enum(['background', 'photos', 'overlay']).optional(),
}) as z.ZodType<ShapeData>;

/**
 * Génère un ID unique pour une shape
 */
export function generateShapeId(): string {
  return `shape_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Crée une shape par défaut selon le type
 */
export function createDefaultShape(type: ShapeType, x: number = 100, y: number = 100): ShapeData {
  const baseShape: ShapeData = {
    id: generateShapeId(),
    type,
    x,
    y,
    opacity: 1,
    visible: true,
    locked: false,
  };

  switch (type) {
    case 'rect':
      return {
        ...baseShape,
        width: 150,
        height: 100,
        fill: '#3b82f6',
        cornerRadius: 0,
      };

    case 'circle':
      return {
        ...baseShape,
        width: 100,
        height: 100,
        fill: '#10b981',
      };

    case 'text':
      return {
        ...baseShape,
        text: 'Nouveau texte',
        fontSize: 24,
        fontFamily: 'Arial',
        fill: '#1f2937',
        textAlign: 'left',
      };

    case 'triangle':
      return {
        ...baseShape,
        width: 100,
        height: 100,
        fill: '#f59e0b',
      };

    case 'star':
      return {
        ...baseShape,
        width: 100,
        height: 100,
        fill: '#eab308',
      };

    case 'heart':
      return {
        ...baseShape,
        width: 100,
        height: 100,
        fill: '#ef4444',
      };

    case 'arrow':
      return {
        ...baseShape,
        width: 100,
        height: 20,
        fill: '#6366f1',
      };

    case 'image':
      return {
        ...baseShape,
        width: 200,
        height: 150,
        src: '',
      };

    case 'photoSpace':
      return {
        ...baseShape,
        width: 200,
        height: 150,
        fill: '#e5e7eb',
        photoIndex: 1,
      };

    default:
      return baseShape;
  }
}
